/**
 * PlatformGoogleRating — Iter 75.
 *
 * "RentalCar.al on Google" block: the platform's OWN Google Business
 * Profile rating + a small carousel of its Google reviews. Rendered at the
 * top of the merged Reviews section on the homepage. Self-hides until the
 * admin configures a Place ID AND the Places API returns a rating.
 */
import { useEffect, useState } from "react";
import { Star, ExternalLink, Quote, BadgeCheck } from "lucide-react";

const PAGE_SIZE = 3;
const ROTATE_MS = 7000;

export default function PlatformGoogleRating({ data }) {
  const [page, setPage] = useState(0);
  const [paused, setPaused] = useState(false);

  const reviews = (data?.reviews || []).filter((r) => (r.text || "").trim());
  const pageCount = Math.max(1, Math.ceil(reviews.length / PAGE_SIZE));

  useEffect(() => {
    if (pageCount <= 1 || paused) return undefined;
    const t = setInterval(() => setPage((p) => (p + 1) % pageCount), ROTATE_MS);
    return () => clearInterval(t);
  }, [pageCount, paused]);

  if (!data || !data.enabled || !data.configured || !data.rating) return null;

  const rating = Number(data.rating);
  const total = Number(data.total || 0);
  const stars = Math.round(rating);
  const visible = reviews.slice(page * PAGE_SIZE, page * PAGE_SIZE + PAGE_SIZE);

  return (
    <div
      className="mb-10 rounded-3xl border border-sky-300/30 bg-gradient-to-br from-sky-500/10 via-white/[0.04] to-transparent backdrop-blur-sm p-6 sm:p-8"
      data-testid="platform-google-rating"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
        <div className="flex items-center gap-5 min-w-0">
          <div className="h-16 w-16 sm:h-20 sm:w-20 rounded-2xl bg-white flex items-center justify-center shrink-0 ring-2 ring-sky-300/40">
            <GoogleG className="h-9 w-9 sm:h-11 sm:w-11" />
          </div>
          <div className="min-w-0">
            <div className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-sky-400/20 text-sky-200 text-[10px] font-bold uppercase tracking-[0.18em] mb-2 ring-1 ring-sky-300/30">
              <BadgeCheck className="h-3 w-3" /> Official Google rating
            </div>
            <div className="font-display text-2xl sm:text-3xl font-semibold text-white truncate" data-testid="platform-google-name">
              {data.display_name || "RentalCar.al"}
            </div>
            <div className="flex items-center gap-2 mt-1.5 flex-wrap">
              <span className="font-display text-2xl sm:text-3xl font-semibold text-amber-300 tabular-nums" data-testid="platform-google-rating-value">
                {rating.toFixed(1)}
              </span>
              <span className="text-white/50 text-lg">/ 5</span>
              <div className="flex items-center gap-0.5">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className={`h-4 w-4 sm:h-5 sm:w-5 ${i < stars ? "fill-amber-400 text-amber-400" : "text-white/25"}`} />
                ))}
              </div>
              <span className="text-sm text-white/70" data-testid="platform-google-review-count">
                Based on {total.toLocaleString()} Google review{total === 1 ? "" : "s"}
              </span>
            </div>
          </div>
        </div>

        <div className="flex flex-wrap gap-2 shrink-0">
          {data.maps_url && (
            <a
              href={data.maps_url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-white text-neutral-900 hover:bg-sky-100 font-semibold text-sm transition"
              data-testid="platform-google-maps-link"
            >
              Read reviews on Google
              <ExternalLink className="h-3.5 w-3.5" />
            </a>
          )}
          {data.write_review_url && (
            <a
              href={data.write_review_url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-white/10 hover:bg-white/20 text-white font-semibold text-sm ring-1 ring-white/20 transition"
              data-testid="platform-google-write-review-link"
            >
              <Star className="h-3.5 w-3.5 fill-amber-400 text-amber-400" />
              Rate us
            </a>
          )}
        </div>
      </div>

      {visible.length > 0 && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6" data-testid="platform-google-reviews-carousel">
            {visible.map((r, i) => (
              <PlatformReviewTile key={`${page}-${i}`} review={r} index={i} />
            ))}
          </div>
          {pageCount > 1 && (
            <div className="flex items-center justify-center gap-2 mt-5" data-testid="platform-google-reviews-dots">
              {[...Array(pageCount)].map((_, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => setPage(i)}
                  aria-label={`Reviews page ${i + 1}`}
                  className={`h-1.5 rounded-full transition-all ${i === page ? "w-8 bg-sky-300" : "w-1.5 bg-white/30 hover:bg-white/60"}`}
                  data-testid={`platform-google-reviews-dot-${i}`}
                />
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function PlatformReviewTile({ review, index }) {
  const stars = Math.round(Number(review.rating) || 0);
  return (
    <div
      className="relative rounded-2xl bg-white/[0.05] border border-white/10 p-5 hover:bg-white/[0.09] transition"
      data-testid={`platform-google-review-${index}`}
    >
      <Quote className="absolute top-4 right-4 h-5 w-5 text-sky-300/30" />
      <div className="flex items-center gap-0.5 mb-3">
        {[...Array(5)].map((_, i) => (
          <Star key={i} className={`h-3.5 w-3.5 ${i < stars ? "fill-amber-400 text-amber-400" : "text-white/20"}`} />
        ))}
      </div>
      <p className="text-[13px] leading-relaxed text-white/85 line-clamp-6 mb-4">
        “{review.text}”
      </p>
      <div className="pt-3 border-t border-white/10 text-xs text-white/70 flex items-center gap-2">
        {review.author_photo ? (
          <img src={review.author_photo} alt="" className="h-7 w-7 rounded-full object-cover" />
        ) : (
          <div className="h-7 w-7 rounded-full bg-sky-400/30 flex items-center justify-center text-[11px] font-semibold text-white">
            {(review.author || "G").trim()[0]?.toUpperCase()}
          </div>
        )}
        <div className="min-w-0">
          <div className="font-medium text-white truncate">{review.author || "Google reviewer"}</div>
          {review.relative_time && (
            <div className="text-white/50 text-[11px]">{review.relative_time}</div>
          )}
        </div>
      </div>
    </div>
  );
}

function GoogleG({ className = "h-6 w-6" }) {
  return (
    <svg className={className} viewBox="0 0 48 48" aria-hidden>
      <path fill="#FFC107" d="M43.611,20.083H42V20H24v8h11.303c-1.649,4.657-6.08,8-11.303,8c-6.627,0-12-5.373-12-12s5.373-12,12-12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C12.955,4,4,12.955,4,24s8.955,20,20,20s20-8.955,20-20C44,22.659,43.862,21.35,43.611,20.083z"/>
      <path fill="#FF3D00" d="M6.306,14.691l6.571,4.819C14.655,15.108,18.961,12,24,12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C16.318,4,9.656,8.337,6.306,14.691z"/>
      <path fill="#4CAF50" d="M24,44c5.166,0,9.86-1.977,13.409-5.192l-6.19-5.238C29.211,35.091,26.715,36,24,36c-5.202,0-9.619-3.317-11.283-7.946l-6.522,5.025C9.505,39.556,16.227,44,24,44z"/>
      <path fill="#1976D2" d="M43.611,20.083H42V20H24v8h11.303c-0.792,2.237-2.231,4.166-4.087,5.571c0.001-0.001,0.002-0.001,0.003-0.002l6.19,5.238C36.971,39.205,44,34,44,24C44,22.659,43.862,21.35,43.611,20.083z"/>
    </svg>
  );
}
